import React, { useState, useEffect, useRef } from "react";
import Icon from "../../components/ui/icon";
import { useToast } from "../../context/toastContext";
import { useNavigate } from "react-router-dom";
import api from "../../services/apiClient";
import "../../assets/styles/pages/admin/publishers.css";
import DOMPurify from "dompurify";
import AdminSideNav from "../../components/layout/adminSideNav";

const AdminApprovedPublisherPage = () => {
    const navigate = useNavigate();
    const showToast = useToast();
    const [isSideNavActive, setisSideNavActive] = useState(false);
    const [userInfo, setUserInfo] = useState(
        JSON.parse(localStorage.getItem("AdminInfo")) || {}
    );
    const [userVerified, setUserVerified] = useState(false);
    const [publishers, setPublishers] = useState([]);
    const [skip, setSkip] = useState(0);
    const limit = 20;
    const [fetchStatus, setfetchStatus] = useState("fetching_first");
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(null);
    const [isUpdatingRole, setIsUpdatingRole] = useState(null);
    const [pubSearchTerm, setpubSearchTerm] = useState("");
    const [searchStatus, setSearchStatus] = useState("idle");
    const [prevSearched, setPrevSearched] = useState(false);
    const [selectedPublisher, setSelectedPublisher] = useState(null);
    const [editing, setEditing] = useState(false);
    const [postback, setPostback] = useState(selectedPublisher?.postback || "");
    const [savingPostback, setSavingPostback] = useState(false);
    const isLoadingMore = useRef(false);

    useEffect(() => {
        const token = localStorage.getItem("aToken");
        if (!token) {
            navigate("/auth/login");
            return;
        }

        const validateUser = async () => {
            if (userVerified) return;

            api
                .get(`/api/admin/info`)
                .then((response) => {
                    setUserInfo(response.data.data);
                    setUserVerified(true);
                    localStorage.setItem("AdminInfo", JSON.stringify(response.data.data));
                    fetchPublishers();
                })
                .catch((err) => {
                    showToast({
                        status: "error",
                        message: err.response?.data?.message || "Login not found.",
                    });
                    navigate("/auth/login");
                });
        };

        const fetchPublishers = async () => {
            if (fetchStatus === "fetching") return;
            setfetchStatus("fetching");

            api
                .get(`/api/a/publisher/?limit=${limit}&skip=${skip}&status=active`)
                .then((response) => {
                    setPublishers((prev) => [...prev, ...response.data.data]);
                    setSkip((prev) => prev + limit);
                    setfetchStatus("fetched");

                    isLoadingMore.current = false;

                    if (response.data.data.length < limit) {
                        setfetchStatus("no_more");
                    }
                })
                .catch((err) => {
                    showToast({
                        status: "error",
                        message:
                            err?.response?.data?.message || "Error Fetching Publishers",
                    });
                });
        };

        const handelScroll = () => {
            const scrollPosition = window.innerHeight + window.scrollY;
            const threshold = document.body.offsetHeight - 150;

            if (scrollPosition >= threshold) {
                if (
                    fetchStatus === "fetching" ||
                    fetchStatus === "fetching_first" ||
                    fetchStatus === "stopped" ||
                    fetchStatus === "no_more" ||
                    isLoadingMore.current
                ) return;

                isLoadingMore.current = true;
                fetchPublishers();
            }
        };

        validateUser();

        window.addEventListener("scroll", handelScroll);
        return () => window.removeEventListener("scroll", handelScroll);
    }, [navigate, showToast, userVerified, fetchStatus, skip]);

    const sanitizeInput = (value) => DOMPurify.sanitize(value);

    const handelUpdateAccStatus = (id, email, status) => {
        if (isUpdatingStatus) return;
        setIsUpdatingStatus(id);

        if (status !== "active" && status !== "suspended") {
            showToast({
                status: "error",
                message: "Invalid status value",
            });
            setIsUpdatingStatus(null);
            return;
        }

        if (status === "suspended" && email === userInfo.email) {
            showToast({
                status: "error",
                message: "You cannot suspend your own account",
            });
            setIsUpdatingStatus(null);
            return;
        }

        api
            .patch(`/api/a/publisher/${id}/status`, { status })
            .then((response) => {
                showToast({ status: "success", message: response.data.message });
                setPublishers((prev) =>
                    prev.map((publisher) => {
                        if (publisher.publisherId === id) {
                            return { ...publisher, status };
                        }
                        return publisher;
                    })
                );
                setSelectedPublisher((prev) => ({
                    ...prev,
                    status: status,
                }));
            })
            .catch((err) => {
                showToast({
                    status: "error",
                    message: err?.response?.data?.message || "Server error",
                });
            })
            .finally(() => {
                setIsUpdatingStatus(null);
            });
    };

    const handelSearchPublisher = () => {
        if (searchStatus === "searching") return;
        setSearchStatus("searching");

        const searchTerm = sanitizeInput(pubSearchTerm.trim());

        if (prevSearched && searchTerm === "") {
            setPrevSearched(false);
            setPublishers([]);
            setSkip(0);
            setfetchStatus("fetching_first");
            setSearchStatus("idle");
            fetchPublishersOut(20, 0);
            return;
        }

        if (!searchTerm || searchTerm.length < 3) {
            showToast({
                status: "error",
                message: "Please enter atleast 3 chracters to search.",
            });
            setSearchStatus("idle");
            return;
        }

        api
            .get(`/api/a/publisher/searchTerm/${searchTerm}`)
            .then((response) => {
                setPublishers(response.data.data || []);
                setSearchStatus("searched");
                setfetchStatus("stopped");
                setPrevSearched(true);
            }).catch((err) => {
                showToast({
                    status: "error",
                    message: err?.response?.data?.message || "Error Fetching Publisher",
                });
            }).finally(() => {
                setSearchStatus("idle");
            });
    }

    const fetchPublishersOut = async (flimit, fskip) => {
        if (fetchStatus === "fetching") return;
        setfetchStatus("fetching");

        api
            .get(`/api/a/publisher/?limit=${flimit}&skip=${fskip}&status=active`)
            .then((response) => {
                setPublishers((prev) => [...prev, ...response.data.data]);
                setSkip(fskip + flimit);
                setfetchStatus("fetched");

                if (response.data.data.length < limit) {
                    setfetchStatus("no_more");
                }
            })
            .catch((err) => {
                showToast({
                    status: "error",
                    message:
                        err?.response?.data?.message || "Error Fetching Publishers",
                });
            });
    };

    const handelCopyInvitelinkClick = () => {
        if (!userInfo || !userInfo?.adminId) return;
        navigator.clipboard.writeText(`https://publishers.swiftdigitalads.com/auth/register/${userInfo?.adminId}`);
        showToast({
            status: "success",
            message: "Copied to clipboard.",
        });
    }

    async function updateGlobalPostback(publisherId, postbackUrl) {
        if (savingPostback) return;
        setSavingPostback(true);
        try {

            const res = await api.put("/api/a/publisher/update-postback", {
                url: postbackUrl,
                publisherId,
            });

            if (!res.data.status) {
                showToast({
                    status: "error",
                    message: res.data.message || "Failed to update postback",
                });
                setSavingPostback(false);
                return false;
            }

            setPublishers((prev) =>
                prev.map((p) =>
                    p.publisherId === publisherId ? { ...p, globalPostback: postbackUrl } : p
                )
            );

            setSelectedPublisher((prev) =>
                prev ? { ...prev, globalPostback: postbackUrl } : prev
            );

            showToast({
                status: "success",
                message: "Postback saved successfully",
            });

            setSavingPostback(false);
            return true;
        } catch (error) {
            showToast({
                status: "error",
                message: error?.response?.data?.message || "Server error",
            });
            setSavingPostback(false);
            return false;
        }
    }

    const handleUpdateRole = (id, role) => {
        if (isUpdatingRole) return;
        setIsUpdatingRole(id);

        if (role !== "publisher" && role !== "employee") {
            showToast({
                status: "error",
                message: "Invalid role value",
            });
            setIsUpdatingRole(null);
            return;
        }

        api
            .patch(`/api/a/publisher/${id}/role`, { role })
            .then((response) => {
                showToast({
                    status: "success",
                    message: response.data.message,
                });

                setPublishers((prev) =>
                    prev.map((publisher) =>
                        publisher.publisherId === id
                            ? { ...publisher, role }
                            : publisher
                    )
                );

                setSelectedPublisher((prev) => ({
                    ...prev,
                    role,
                }));
            })
            .catch((err) => {
                showToast({
                    status: "error",
                    message:
                        err?.response?.data?.message || "Server error while updating role",
                });
            })
            .finally(() => {
                setIsUpdatingRole(null);
            });
    };

    return (
        <div className="publishers__page">
            <AdminSideNav
                userName={userInfo.name}
                emailID={userInfo.email}
                active={isSideNavActive}
                initialPublishers={true}
            />
            <div
                onClick={() => setisSideNavActive(!isSideNavActive)}
                className={
                    isSideNavActive ? "background-shadow active" : "background-shadow"
                }
            ></div>

            <main>
                <header className="desktop-header">
                    <div>
                        <h2>Publishers</h2>
                        <p>Manage your active publishers easily in only some clicks.</p>
                    </div>
                    <button onClick={handelCopyInvitelinkClick}>
                        <Icon name="Link" className="onLeft" /> Copy Invite Link
                    </button>
                </header>

                <header className="mobile-header">
                    <div>
                        <h2>Publishers</h2>
                    </div>
                    <button
                        className="nav-btn"
                        onClick={() => setisSideNavActive(!isSideNavActive)}
                    >
                        {isSideNavActive ? (
                            <Icon name="XMark" className="onLeft" />
                        ) : (
                            <Icon name="Bars3" className="onLeft" />
                        )}
                    </button>
                </header>

                <div className="search-box">
                    {searchStatus === "searching" ? <Icon name="ArrowPath" className="spin" /> : <Icon name="MagnifyingGlass" />}
                    <input
                        type="email"
                        placeholder="Search for publishers by name,email id or country."
                        autoComplete="off"
                        value={pubSearchTerm}
                        onChange={(e) => setpubSearchTerm(sanitizeInput(e.target.value))}
                        onKeyDown={(e) => { if (e.key === 'Enter') handelSearchPublisher() }}
                    />
                </div>

                <div className="publisher-container">
                    <div className="publisher-grid">
                        {publishers.length > 0 ? (
                            publishers.map((pub) => (
                                <div
                                    key={pub.publisherId}
                                    className={`publisher-card ${pub.status === "active" ? "approved" : pub.status === "pending" ? "pending" : "suspended"
                                        }`}
                                >
                                    <div className="publisher-info">
                                        <h3 className="publisher-name">{pub.name}</h3>
                                        <p className="publisher-email">{pub.email}</p>
                                        <p className="publisher-meta">
                                            <strong>Last Online:</strong> {pub.lastOnline}
                                        </p>
                                        <p className="publisher-meta">
                                            <strong>Total Clicks:</strong> {pub.totalClickGot}
                                        </p>
                                        <p className="publisher-meta">
                                            <strong>Conversions:</strong> {pub.totalLeadsGenerated}
                                        </p>
                                    </div>

                                    <button
                                        className={`status-btn approve-btn`}
                                        onClick={() => { setSelectedPublisher(pub); setPostback(pub?.globalPostback || ""); setEditing(false); }}
                                    >More Info
                                    </button>
                                </div>
                            ))
                        ) : fetchStatus !== "fetching" && fetchStatus !== "fetching_first" ? (
                            <div className="flat-message">
                                <Icon name="Users" className="msg-icon" />
                                <h3 className="title">No Publishers Available</h3>
                                <p className="description">
                                    Opps! Seems like no one is registered as publisher.
                                </p>
                            </div>
                        ) : (
                            ""
                        )}
                        {fetchStatus === "fetching" || fetchStatus === "fetching_first" ? (
                            <>
                                <div className="publisher-card loading"></div>
                                <div className="publisher-card loading"></div>
                                <div className="publisher-card loading"></div>
                                <div className="publisher-card loading"></div>
                                <div className="publisher-card loading"></div>
                                <div className="publisher-card loading"></div>
                            </>
                        ) : (
                            ""
                        )}
                    </div>
                </div>

                <div
                    className={`publisher-modal-overlay ${selectedPublisher ? "active" : ""}`}
                    onClick={() => setSelectedPublisher(null)}
                ></div>

                <div
                    className={`publisher-modal ${selectedPublisher ? "active" : ""}`}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="publisher-modal-header">
                        <h2>Publisher Details</h2>
                        <button className="icon-btn" onClick={() => setSelectedPublisher(null)}>
                            <Icon name="XMark" />
                        </button>
                    </div>

                    <div className="publisher-modal-body">

                        <section className="modal-section">
                            <h3>Basic Information</h3>

                            <div className="info-grid">
                                <div>
                                    <label>Name</label>
                                    <p>{selectedPublisher?.name}</p>
                                </div>

                                <div>
                                    <label>Email</label>
                                    <p>{selectedPublisher?.email}</p>
                                </div>

                                <div>
                                    <label>Mobile</label>
                                    <p>{selectedPublisher?.phone}</p>
                                </div>

                                <div>
                                    <label>Country</label>
                                    <p>{selectedPublisher?.country}</p>
                                </div>

                                <div>
                                    <label>Status</label>
                                    <p className={`status-tag ${selectedPublisher?.status}`}>
                                        {selectedPublisher?.status}
                                    </p>
                                </div>
                            </div>
                        </section>

                        {selectedPublisher?.contact?.length > 0 && (
                            <section className="modal-section">
                                <h3>Contact Handles</h3>

                                <div className="contact-list">
                                    {selectedPublisher.contact.map((item, index) => (
                                        <div className="contact-item" key={index}>
                                            <span className="contact-platform">
                                                {item.platform}
                                            </span>
                                            <span className="contact-username">
                                                {item.username}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}

                        <section className="modal-section">
                            <h3>Traffic Source</h3>
                            <p className="traffic-text">
                                {selectedPublisher?.trafficSource || "Not Available"}
                            </p>
                        </section>

                        <section className="modal-section">
                            <h3>Global Postback</h3>

                            <textarea
                                className={`postback-area ${editing ? "editable" : ""}`}
                                readOnly={!editing}
                                value={postback}
                                onChange={(e) => setPostback(e.target.value)}
                                onClick={() => setEditing(true)}
                            />

                            {editing && (
                                <button
                                    className="save-pb-btn"
                                    onClick={() =>
                                        updateGlobalPostback(
                                            selectedPublisher?.publisherId,
                                            postback
                                        )
                                    }
                                >
                                    {savingPostback ? (
                                        <Icon name="ArrowPath" className="spin" />
                                    ) : (
                                        "Save Postback"
                                    )}
                                </button>
                            )}
                        </section>
                    </div>

                    {/* FOOTER ACTIONS */}
                    <div className="publisher-modal-footer">
                        {userInfo?.role === "admin" && (
                            <button
                                className="status-action-btn"
                                onClick={() =>
                                    handleUpdateRole(
                                        selectedPublisher?.publisherId,
                                        selectedPublisher?.role === "publisher"
                                            ? "employee"
                                            : "publisher"
                                    )
                                }
                            >
                                {isUpdatingRole === selectedPublisher?.publisherId && (
                                    <Icon name="ArrowPath" className="spin" />
                                )}
                                {selectedPublisher?.role === "publisher"
                                    ? "Make Employee"
                                    : "Make Publisher"}
                            </button>
                        )}

                        <button className={`status-action-btn ${selectedPublisher?.status === "active" ? "suspend-btn" : "approve-btn"}`} onClick={() => handelUpdateAccStatus(selectedPublisher?.publisherId, selectedPublisher?.email, selectedPublisher.status === "active" ? "suspended" : "active")}>{isUpdatingStatus === selectedPublisher?.publisherId ? (<Icon name="ArrowPath" className="spin" />) : ("")} {selectedPublisher?.status === "active" ? "Suspend" : selectedPublisher?.status === "pending" ? "Approve" : "Approve"} </button>

                        <button className="close-modal-btn" onClick={() => setSelectedPublisher(null)}> Close </button>
                    </div>
                </div>
                <span className="copyright-text">
                    © 2025 Swift Digital Ads, All rights reserved
                </span>
            </main>
        </div>
    );
};

export default AdminApprovedPublisherPage;